<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

@let canSeeEfficiency = authorizationService.canSee(grants.efficiencyMetrics);
@let canSeeKeyMetrics = authorizationService.canSee(grants.keyMetrics);
@let canSeeEnergyTrend = authorizationService.canSee(grants.energyConsumptionAndEmissions);
@let canSeeEnergyBreakdown = authorizationService.canSee(grants.energyBreakdown);
@let canSeeSiteMetrics =
	canSeeEfficiency || canSeeKeyMetrics || canSeeEnergyTrend || canSeeEnergyBreakdown;
@let canSeeOverview = authorizationService.canSee(grants.overview);
@let canSeeSummary = authorizationService.canSee(grants.summary);

<section class="data-centers-page">
	@if (canSeeOverview) {
		@let overviewCards = overviewCards$ | async;

		<section class="overview-section">
			<span class="section-title section-title--page">Data Centers Overview</span>
			<div class="overview-grid">
				@for (card of overviewCards?.view ?? []; track card.key) {
					<div class="overview-card">
						<div class="card-header overview-card-header">
							<span class="card-title">{{ getOverviewTitle(card.key) }}</span>
							@if (hasOverviewInfo(card.key)) {
								<button
									type="button"
									class="icon-info-trigger"
									[attr.aria-label]="
										'Open info for ' + getOverviewTitle(card.key)
									"
									(click)="onOpenMetricInfoDialog(card.key)"
								>
									<span class="icon-warning"></span>
								</button>
							}
						</div>
						<globalictcc-kpi
							[value]="card.value ?? 0"
							[unit]="card.unit"
							[useGrouping]="true"
						/>
					</div>
				}
			</div>
		</section>
	}

	@if (canSeeSummary) {
		@let summary = summary$ | async;

		<span class="page-title">Summary</span>

		<section class="summary-grid">
			<div class="map-panel">
				<globalictcc-map
					[data]="summary?.mapChart?.chart"
					[configOverrides]="summaryMapOverrides"
					[hasError]="summary?.mapChart?.hasError"
					[isLoading]="summary?.isLoading ?? true"
					[isLegendVisible]="false"
					[showPointPopupOnClick]="true"
				/>
			</div>

			<div class="table-panel">
				<div class="table-header">
					<div class="table-cell table-cell--header">Site</div>
					<div class="table-cell table-cell--header">PUE</div>
					<div class="table-cell table-cell--header">CUE</div>
					<div class="table-cell table-cell--header">WUE</div>
				</div>
				@for (row of summary?.tableRows ?? []; track row.site) {
					<div class="table-row">
						<div class="table-cell">{{ row.site }}</div>
						<div
							class="table-cell"
							[class]="row.pueClass"
						>
							{{ row.pue !== null ? (row.pue | maxDecimals: 3 : true) : "-" }}
						</div>
						<div
							class="table-cell"
							[class]="row.cueClass"
						>
							{{ row.cue !== null ? (row.cue | maxDecimals: 3 : true) : "-" }}
						</div>
						<div
							class="table-cell"
							[class]="row.wueClass"
						>
							{{ row.wue !== null ? (row.wue | maxDecimals: 3 : true) : "-" }}
						</div>
					</div>
				}
			</div>
		</section>
	}

	@if (canSeeOverview || canSeeSummary || canSeeSiteMetrics) {
		<section class="sites-section">
			<span class="section-title section-title--page">Sites</span>
			@if (canSeeSiteMetrics) {
				<div class="filters-row">
					<globalictcc-dropdown
						label="Site"
						[options]="siteOptions"
						[formControl]="siteControl"
					/>
				</div>
			}
			<div class="filters-row">
				<globalictcc-dropdown
					label="Water unit"
					[options]="waterUnitOptions"
					[formControl]="waterUnitControl"
				/>
				<globalictcc-dropdown
					label="Emission unit"
					[options]="emissionUnitOptions"
					[formControl]="emissionUnitControl"
				/>
				<globalictcc-dropdown
					label="Power unit"
					[options]="powerUnitOptions"
					[formControl]="powerUnitControl"
				/>
				@if (canSeeSiteMetrics) {
					<globalictcc-button
						label="Apply"
						(handleClick)="onApply()"
					/>
				}
			</div>
		</section>
	}

	@if (canSeeEfficiency) {
		@let efficiency = efficiencyCards$ | async;

		<section class="efficiency-section">
			<span class="section-title section-title--page">Efficiency Metrics</span>
			<div class="efficiency-cards">
				@for (card of efficiency?.view ?? []; track card.key) {
					<div class="efficiency-card">
						<div class="card-header">
							<span class="card-title">{{ getEfficiencyTitle(card.key) }}</span>
							<button
								type="button"
								class="icon-info-trigger"
								[attr.aria-label]="'Open info for ' + getEfficiencyTitle(card.key)"
								(click)="onOpenMetricInfoDialog(card.key)"
							>
								<span class="icon-warning"></span>
							</button>
						</div>
						<div class="card-current">
							<span class="card-current-label">Current</span>
							<globalictcc-kpi
								[value]="card.current ?? 0"
								[unit]="card.unit"
								[useGrouping]="true"
								[maxDecimals]="3"
							/>
						</div>
						<div class="card-summary-grid">
							<div class="summary-stat">
								<span class="summary-label">Previous</span>
								<span class="summary-value">
									{{ card.previous ?? 0 | maxDecimals: 3 : true }}
									@if (card.unit) {
										<span class="summary-unit">{{ card.unit }}</span>
									}
								</span>
							</div>
							<div class="summary-stat">
								<span class="summary-label">Change</span>
								<span
									class="summary-value"
									[class.summary-value--positive]="(card.change ?? 0) < 0"
									[class.summary-value--negative]="(card.change ?? 0) > 0"
								>
									@if ((card.change ?? 0) > 0) {
										+
									}
									{{ card.change ?? 0 | maxDecimals: 3 : true }}%
								</span>
							</div>
							<div class="summary-stat">
								<span class="summary-label">Annual Average</span>
								<span class="summary-value">
									{{ card.annualAverage ?? 0 | maxDecimals: 3 : true }}
									@if (card.unit) {
										<span class="summary-unit">{{ card.unit }}</span>
									}
								</span>
							</div>
						</div>
						<div class="card-chart">
							<globalictcc-chart
								chartType="line"
								[data]="card.chart?.chart"
								[isLoading]="efficiency?.isLoading ?? true"
								[hasError]="card.chart?.hasError"
								[configOverrides]="efficiencyChartOverrides"
								[colors]="[card.color]"
								[height]="'220px'"
								[useGrouping]="true"
							/>
						</div>
					</div>
				}
			</div>
		</section>
	}

	@if (canSeeKeyMetrics) {
		@let keyMetrics = keyMetricsCards$ | async;

		<section class="efficiency-section">
			<span class="section-title section-title--page">Key Metrics</span>
			<div class="efficiency-cards">
				@for (card of keyMetrics?.view ?? []; track card.key) {
					<div class="efficiency-card">
						<div class="card-header">
							<span class="card-title">{{ getKeyMetricsTitle(card.key) }}</span>
							<button
								type="button"
								class="icon-info-trigger"
								[attr.aria-label]="'Open info for ' + getKeyMetricsTitle(card.key)"
								(click)="onOpenMetricInfoDialog(card.key)"
							>
								<span class="icon-warning"></span>
							</button>
						</div>
						<div class="card-current">
							<span class="card-current-label">Current</span>
							<globalictcc-kpi
								[value]="card.current ?? 0"
								[unit]="card.unit"
								[useGrouping]="true"
							/>
						</div>
						<div class="card-summary-grid">
							<div class="summary-stat">
								<span class="summary-label">Previous</span>
								<span class="summary-value">
									{{ card.previous ?? 0 | maxDecimals: 3 : true }}
									@if (card.unit) {
										<span class="summary-unit">{{ card.unit }}</span>
									}
								</span>
							</div>
							<div class="summary-stat">
								<span class="summary-label">Change</span>
								<span
									class="summary-value"
									[class.summary-value--positive]="(card.change ?? 0) < 0"
									[class.summary-value--negative]="(card.change ?? 0) > 0"
								>
									@if ((card.change ?? 0) > 0) {
										+
									}
									{{ card.change ?? 0 | maxDecimals: 3 : true }}%
								</span>
							</div>
							<div class="summary-stat">
								<span class="summary-label">Annual Average</span>
								<span class="summary-value">
									{{ card.annualAverage ?? 0 | maxDecimals: 3 : true }}
									@if (card.unit) {
										<span class="summary-unit">{{ card.unit }}</span>
									}
								</span>
							</div>
						</div>
						<div class="card-chart">
							<globalictcc-chart
								chartType="line"
								[data]="card.chart?.chart"
								[isLoading]="keyMetrics?.isLoading ?? true"
								[hasError]="card.chart?.hasError"
								[configOverrides]="efficiencyChartOverrides"
								[colors]="[card.color]"
								[height]="'220px'"
								[useGrouping]="true"
							/>
						</div>
					</div>
				}
			</div>
		</section>
	}

	@if (canSeeEnergyTrend) {
		@let energyTrend = energyTrendChart$ | async;

		<section class="energy-section">
			<span class="section-title">Energy Consumption and Emissions</span>
			<globalictcc-chart
				chartType="line"
				[data]="energyTrend?.view?.chart"
				[isLoading]="energyTrend?.isLoading ?? true"
				[hasError]="energyTrend?.view?.hasError"
				[isLegendVisible]="true"
				[configOverrides]="energyTrendOverrides"
				[colors]="energyTrendColors"
				[height]="'360px'"
				[useGrouping]="true"
			/>
		</section>
	}

	@if (canSeeEnergyBreakdown) {
		@let energyBreakdown = energyBreakdownChart$ | async;

		<section class="energy-section">
			<span class="section-title">Energy Breakdown</span>
			<globalictcc-chart
				chartType="barX"
				[data]="energyBreakdown?.view?.chart"
				[isLoading]="energyBreakdown?.isLoading ?? true"
				[hasError]="energyBreakdown?.view?.hasError"
				[isLegendVisible]="true"
				[configOverrides]="energyBreakdownOverrides"
				[colors]="energyBreakdownColors"
				[patterns]="energyBreakdownPatterns"
				[height]="'360px'"
				[useGrouping]="true"
			/>
		</section>
	}

	<div class="bottom-actions">
		<globalictcc-button
			label="Export report"
			iconName="document"
			[textOnly]="true"
			(handleClick)="onExportReport()"
		/>
	</div>
</section>
